// artifacts/edison/src/pages/claims/claim-activities-section.tsx
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Layers, Pencil, Lock, Plus, Trash2, AlertTriangle } from 'lucide-react';
import {
  useGetClaimActivities, usePutClaimActivityMapping, usePatchClaimActivity, getGetClaimActivitiesQueryKey, getGetClaimProjectGroupsQueryKey,
  type ClaimActivitiesView, type ProjectActivityMapping,
} from '@edison/api-client-react';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';

const fmt = (n: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
const hrs = (n: number) => `${(Math.round(n * 10) / 10).toFixed(1)}h`;
const label = (type: string, number: number) => `${type === 'core' ? 'Core' : 'Supporting'} ${number}`;
const errMsg = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? fallback;

interface Props { claimId: string }

/**
 * Claim activity roll-up (spec 2026-10-01): the project → Core/Supporting 1-3 mapping with the
 * working-paper proposal, and the six-bucket roll-up reconciling to the claim labour total with
 * non-labour as one separate line. A locked claim shows its frozen snapshot read-only. Edits
 * happen in a SlidePanel, never a modal.
 */
export function ClaimActivitiesSection({ claimId }: Props) {
  const qc = useQueryClient();
  const query = useGetClaimActivities(claimId);
  const putMapping = usePutClaimActivityMapping();
  const patchActivity = usePatchClaimActivity();
  const [editing, setEditing] = useState<{ projectId: string; projectName: string } | null>(null);
  const [renaming, setRenaming] = useState<{ number: number; activityType: 'core' | 'supporting'; name: string } | null>(null);
  const [error, setError] = useState('');
  const [panelError, setPanelError] = useState('');
  const openEditing = (p: { projectId: string; projectName: string }) => { setError(''); setPanelError(''); setEditing(p); };
  const closeEditing = () => { setPanelError(''); setEditing(null); };
  const openRenaming = (r: { number: number; activityType: 'core' | 'supporting'; name: string }) => { setError(''); setPanelError(''); setRenaming(r); };
  const closeRenaming = () => { setPanelError(''); setRenaming(null); };

  const view = query.data?.data as ClaimActivitiesView | undefined;
  const mappingByProject = useMemo(() => {
    const m = new Map<string, ProjectActivityMapping[]>();
    for (const row of view?.mapping ?? []) m.set(row.projectId, [...(m.get(row.projectId) ?? []), row]);
    return m;
  }, [view?.mapping]);

  if (query.isLoading) return <SkeletonCard />;
  if (query.error || !view) return <InlineError message="Failed to load the claim activities." onRetry={() => void query.refetch()} />;

  const locked = view.basis === 'snapshot';
  // The project-groups section shows each member's activity, so a mapping change refreshes it too.
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: getGetClaimActivitiesQueryKey(claimId) });
    void qc.invalidateQueries({ queryKey: getGetClaimProjectGroupsQueryKey(claimId) });
  };
  const save = (projects: Array<{ projectId: string; rows: Array<{ activityNumber: number; fraction: number }> }>, source: 'working_paper' | 'manual') => {
    setError('');
    setPanelError('');
    const fail = source === 'manual' ? setPanelError : setError;
    putMapping.mutate({ id: claimId, data: { source, projects } }, {
      onSuccess: () => { closeEditing(); refresh(); },
      onError: (err) => fail(errMsg(err, 'Failed to save the mapping. Please try again.')),
    });
  };

  const rollUp = view.rollUp;
  const grand = rollUp ? rollUp.labourTotal + rollUp.nonLabourTotal : 0;

  return (
    <Card variant="static" className="p-5 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium flex items-center gap-2"><Layers className="h-4 w-4" aria-hidden="true" /> Claim activities</h3>
        {locked
          ? <Badge><Lock className="h-3.5 w-3.5 mr-1 inline" aria-hidden="true" />Frozen at lock</Badge>
          : view.proposal && (
            <Button variant="secondary" size="sm" disabled={putMapping.isPending || view.proposal.proposed.length === 0}
              onClick={() => save(groupRows(view.proposal!.proposed), 'working_paper')}>
              Load working-paper mapping
            </Button>
          )}
      </div>
      <p className="text-xs text-muted-foreground">
        Each claimable project rolls up to one or more claim activities. Core labour goes to Core n, supporting labour to Supporting n, by the project&rsquo;s adopted share. Non-labour is not allocated.
      </p>
      {view.proposal && view.proposal.unmatched.length > 0 && (
        <p className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          Not in the working paper: {view.proposal.unmatched.join(', ')}. Map these by hand.
        </p>
      )}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}

      {/* Activities (rename inline) */}
      <ul className="grid grid-cols-1 gap-1 lg:grid-flow-col lg:grid-cols-2 lg:grid-rows-3 text-xs">
        {view.activities.map((a) => (
          <li key={`${a.activityType}-${a.number}`} className="flex items-center gap-2">
            <span className="font-mono w-24 shrink-0">{label(a.activityType, a.number)}</span>
            <span className="truncate">{a.name}</span>
            {!locked && (
              <button type="button" aria-label={`Rename ${label(a.activityType, a.number)}`} className="text-muted-foreground hover:text-foreground"
                onClick={() => openRenaming({ number: a.number, activityType: a.activityType, name: a.name })}>
                <Pencil className="h-3 w-3" aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {/* Mapping table */}
      {!locked && view.claimableProjects.length > 0 && (
        <table className="w-full text-xs" aria-label="Project to claim activity mapping">
          <thead className="text-muted-foreground">
            <tr><th className="text-left font-medium py-1">Project</th><th className="text-left font-medium py-1">Claim activities</th><th className="py-1" /></tr>
          </thead>
          <tbody>
            {view.claimableProjects.map((p) => {
              const rows = mappingByProject.get(p.id) ?? [];
              return (
                <tr key={p.id} className="border-t">
                  <td className="py-1.5">{p.name}</td>
                  <td className="py-1.5">
                    {rows.length === 0
                      ? <Badge variant="warning"><AlertTriangle className="h-3 w-3 mr-1 inline" aria-hidden="true" />Unmapped</Badge>
                      : rows.map((r) => `Activity ${r.activityNumber}${rows.length > 1 ? ` ${Math.round(r.fraction * 100)}%` : ''}`).join(' · ')}
                  </td>
                  <td className="py-1.5 text-right">
                    <Button variant="ghost" size="sm" aria-label={`Edit mapping for ${p.name}`} onClick={() => openEditing({ projectId: p.id, projectName: p.name })}>
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Roll-up */}
      {rollUp ? (
        <table className="w-full text-xs" aria-label="Claim activity roll-up">
          <thead className="text-muted-foreground">
            <tr><th className="text-left font-medium py-1">Claim activity</th><th className="text-left font-medium py-1">Projects</th><th className="text-right font-medium py-1 pr-4">Hours</th><th className="text-right font-medium py-1">Labour</th></tr>
          </thead>
          <tbody>
            {rollUp.buckets.map((b) => (
              <tr key={`${b.activityType}-${b.number}`} className="border-t">
                <td className="py-1.5"><span className="font-mono">{label(b.activityType, b.number)}</span> <span className="text-muted-foreground">{b.name}</span></td>
                <td className="py-1.5 text-muted-foreground">{b.projects.map((p) => `${p.projectName}${p.fraction < 1 ? ` ${Math.round(p.fraction * 100)}%` : ''}`).join(', ') || '—'}</td>
                <td className="py-1.5 pr-4 text-right font-mono">{hrs(b.rdHours)}</td>
                <td className="py-1.5 text-right font-mono">{fmt(b.rdExpenditure)}</td>
              </tr>
            ))}
            {rollUp.unmapped.length > 0 && (
              <tr className="border-t text-amber-800 dark:text-amber-300">
                <td className="py-1.5"><AlertTriangle className="h-3 w-3 mr-1 inline" aria-hidden="true" />Unmapped</td>
                <td className="py-1.5">{rollUp.unmapped.map((u) => u.projectName).join(', ')}</td>
                <td className="py-1.5 pr-4 text-right font-mono">{hrs(rollUp.unmapped.reduce((s, u) => s + u.rdHours, 0))}</td>
                <td className="py-1.5 text-right font-mono">{fmt(rollUp.unmapped.reduce((s, u) => s + u.rdExpenditure, 0))}</td>
              </tr>
            )}
            <tr className="border-t font-medium"><td className="py-1.5" colSpan={3}>Labour</td><td className="py-1.5 text-right font-mono">{fmt(rollUp.labourTotal)}</td></tr>
            <tr className="border-t"><td className="py-1.5" colSpan={3}>Non-labour (not allocated to activities)</td><td className="py-1.5 text-right font-mono">{fmt(rollUp.nonLabourTotal)}</td></tr>
            <tr className="border-t font-semibold"><td className="py-1.5" colSpan={3}>Total</td><td className="py-1.5 text-right font-mono">{fmt(grand)}</td></tr>
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-muted-foreground">
          {view.unavailableReason === 'LOCKED_WITHOUT_ROLLUP'
            ? 'This claim was locked before claim activities existed; its outputs keep the per-project activities.'
            : view.unavailableReason === 'SHARES_NOT_ADOPTED'
            ? `Roll-up withheld — R&D shares not yet adopted for ${view.awaitingProjects?.length ?? 0} project(s).`
            : 'The roll-up is available for claims using the apportionment method.'}
        </p>
      )}

      {editing && (
        <MappingPanel
          project={editing} rows={(mappingByProject.get(editing.projectId) ?? []).map((r) => ({ activityNumber: r.activityNumber, fraction: r.fraction }))}
          activities={view.activities} error={panelError}
          saving={putMapping.isPending} onClose={closeEditing}
          onSave={(rows) => save([{ projectId: editing.projectId, rows }], 'manual')}
        />
      )}
      {renaming && (
        <SlidePanel open title={`Rename ${label(renaming.activityType, renaming.number)}`} onClose={closeRenaming}>
          <form className="space-y-3 p-4" onSubmit={(e) => {
            e.preventDefault();
            setPanelError('');
            patchActivity.mutate({ id: claimId, number: renaming.number, type: renaming.activityType, data: { name: renaming.name } }, {
              onSuccess: () => { closeRenaming(); refresh(); },
              onError: (err) => setPanelError(errMsg(err, 'Failed to rename the activity.')),
            });
          }}>
            <label className="text-xs font-medium block" htmlFor="claim-activity-name">Name</label>
            <input id="claim-activity-name" className="w-full rounded border bg-background px-3 py-2 text-sm" value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} />
            {panelError && <p role="alert" className="text-xs text-destructive">{panelError}</p>}
            <Button type="submit" variant="primary" size="sm" disabled={patchActivity.isPending || renaming.name.trim() === ''}>Save</Button>
          </form>
        </SlidePanel>
      )}
    </Card>
  );
}

function groupRows(rows: ProjectActivityMapping[]) {
  const by = new Map<string, Array<{ activityNumber: number; fraction: number }>>();
  for (const r of rows) by.set(r.projectId, [...(by.get(r.projectId) ?? []), { activityNumber: r.activityNumber, fraction: r.fraction }]);
  return [...by].map(([projectId, rs]) => ({ projectId, rows: rs }));
}

function MappingPanel({ project, rows: initial, activities, error, saving, onClose, onSave }: {
  project: { projectId: string; projectName: string };
  rows: Array<{ activityNumber: number; fraction: number }>;
  activities: ClaimActivitiesView['activities'];
  error: string;
  saving: boolean;
  onClose: () => void;
  onSave: (rows: Array<{ activityNumber: number; fraction: number }>) => void;
}) {
  // Percent strings in the form; fractions on save.
  const [rows, setRows] = useState(initial.length > 0 ? initial.map((r) => ({ activityNumber: r.activityNumber, pct: String(Math.round(r.fraction * 10000) / 100) })) : [{ activityNumber: 1, pct: '100' }]);
  const optionLabel = (n: number) => {
    const name = activities.find((a) => a.activityType === 'core' && a.number === n)?.name;
    return name ? `${n} · ${name}` : String(n);
  };
  // Integer basis points (1bp = 0.01%) so the shares sum exactly, as the server requires.
  const bps = rows.map((r) => Math.round((parseFloat(r.pct) || 0) * 100));
  const sumBp = bps.reduce((s, b) => s + b, 0);
  const numbers = rows.map((r) => r.activityNumber);
  const problem = bps.some((b) => !(b > 0)) ? 'Each share must be greater than 0%.'
    : new Set(numbers).size !== numbers.length ? 'Each activity can appear only once.'
    : sumBp !== 10000 ? `Shares must sum to exactly 100.00% (currently ${(sumBp / 100).toFixed(2)}%).`
    : null;
  return (
    <SlidePanel open title={`Map ${project.projectName}`} onClose={onClose}>
      <div className="space-y-3 p-4">
        {rows.map((r, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs font-medium block mb-1" htmlFor={`map-activity-${i}`}>Activity</label>
              <select id={`map-activity-${i}`} className="w-full rounded border bg-background px-2 py-2 text-sm" value={r.activityNumber}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, activityNumber: parseInt(e.target.value, 10) } : x)))}>
                <option value={1}>{optionLabel(1)}</option>
                <option value={2}>{optionLabel(2)}</option>
                <option value={3}>{optionLabel(3)}</option>
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs font-medium block mb-1" htmlFor={`map-share-${i}`}>Share %</label>
              <input id={`map-share-${i}`} type="number" min="0" max="100" step="0.01" inputMode="decimal" className="w-full rounded border bg-background px-2 py-2 text-sm" value={r.pct}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, pct: e.target.value } : x)))} />
            </div>
            <Button variant="ghost" size="sm" aria-label="Remove split" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        ))}
        <Button variant="secondary" size="sm" disabled={rows.length >= 3} onClick={() => setRows([...rows, { activityNumber: ([1, 2, 3].find((n) => !numbers.includes(n)) ?? 1), pct: '0' }])}>
          <Plus className="h-3.5 w-3.5 mr-1 inline" aria-hidden="true" />Add split
        </Button>
        {problem && <p className="text-xs text-destructive">{problem}</p>}
        {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
        <div className="flex gap-2 pt-2">
          <Button variant="primary" size="sm" disabled={!!problem || saving} onClick={() => onSave(rows.map((r, i) => ({ activityNumber: r.activityNumber, fraction: bps[i]! / 10000 })))}>Save</Button>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
        </div>
      </div>
    </SlidePanel>
  );
}
